import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Avatar, Box, Button, Center, ProgressCircle, Flex, Heading, HStack, Spinner, Text, VStack, Dialog, Portal } from '@chakra-ui/react';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { paywallPropsForMessagingTarget } from '@src/components/sco/Chat/directMessagePaywall';
import { useMatchingContext } from '@src/components/sco/Matching/Layout/MatchingContext';
import { PaywalledAction } from '@src/components/sco/PaywalledAction';
import { useDelayedText } from '@src/components/sco/TypingText';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { ScoUserMatch, UpsertChatCommentInput, UpsertChatCommentPayload, UpsertDirectChatThreadInput, UpsertDirectChatThreadPayload } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useUserAccessCheck } from '@src/hooks/useUserAccessCheck';
import { gql } from '@src/lib/gql';
import React, { useEffect, useState } from 'react';
export const UserMatchIntroModal = props => {
  const $ = _c(14);
  let t0;
  if ($[0] !== props) {
    t0 = t1 => {
      const {
        open
      } = t1;
      if (!open) {
        props.onClose();
      }
    };
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Dialog.Backdrop />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Dialog.Header><Flex alignItems="center" justifyContent="center" w="full"><Heading size="2xl">Send an Introduction</Heading></Flex></Dialog.Header>;
    t3 = <Dialog.CloseTrigger />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== props) {
    t4 = () => props.onClose();
    $[5] = props;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== props.userMatchId || $[8] !== t4) {
    t5 = <Dialog.Positioner><Dialog.Content bgColor="colorPalette.container" borderRadius="3xl" boxShadow="lg" margin={4} padding={4}>{t2}{t3}<Dialog.Body><UserMatchIntroForm afterSuccess={t4} userMatchId={props.userMatchId} /></Dialog.Body></Dialog.Content></Dialog.Positioner>;
    $[7] = props.userMatchId;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== props.open || $[11] !== t0 || $[12] !== t5) {
    t6 = <Portal><Dialog.Root open={props.open} size="lg" onOpenChange={t0}>{t1}{t5}</Dialog.Root></Portal>;
    $[10] = props.open;
    $[11] = t0;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
const UserMatchIntroForm = props => {
  const $ = _c(84);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      require: {
        contentPermissions: ["canAccessMemberMatching"]
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useUserAccessCheck(t0);
  const {
    currentUser
  } = useCurrentUser();
  const matchingContext = useMatchingContext();
  const [introChatMessage, setIntroChatMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = gql`
      query ($userMatchId: String!) {
        scoUserMatch(userMatchId: $userMatchId) {
          id
          userMatchId
          matchScoreAsPercent
          introChatMessage(generateIfEmpty: true)
          searchingUserId
          searchingUser {
            id
            userId
            fullName
            pictureUrl
          }
          targetUserId
          targetUser {
            id
            userId
            fullName
            pictureUrl
            isAdvisor
            isToolUser
            slug
          }
        }
      }
    `;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props.userMatchId) {
    t2 = {
      variables: {
        userMatchId: props.userMatchId
      }
    };
    $[2] = props.userMatchId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const query = useQuery(t1, t2);
  const userMatch = query.data?.scoUserMatch;
  const t3 = userMatch?.targetUser;
  let t4;
  if ($[4] !== currentUser || $[5] !== t3) {
    t4 = paywallPropsForMessagingTarget(t3, currentUser);
    $[4] = currentUser;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const teamPaywall = t4;
  const delayedIntroChatMessage = useDelayedText(userMatch?.introChatMessage || "Writing draft...");
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = gql`
    mutation upsertChatThread($input: UpsertDirectChatThreadInput!) {
      upsertDirectChatThread(input: $input) {
        thread {
          id
          threadId
        }
        viewer {
          id
          currentUser {
            id
            remainingMemberChatsCount
          }
        }
      }
    }
  `;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const [upsertDirectChatThread] = useMutation(t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = gql`
    mutation ($input: UpsertChatCommentInput!) {
      upsertChatComment(input: $input) {
        comment {
          id
          commentId
        }
      }
    }
  `;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const [upsertChatComment] = useMutation(t6);
  let t7;
  let t8;
  if ($[9] !== delayedIntroChatMessage) {
    t7 = () => {
      if (delayedIntroChatMessage) {
        setIntroChatMessage(delayedIntroChatMessage);
      }
    };
    t8 = [delayedIntroChatMessage];
    $[9] = delayedIntroChatMessage;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] !== introChatMessage || $[13] !== matchingContext || $[14] !== props || $[15] !== teamPaywall || $[16] !== upsertChatComment || $[17] !== upsertDirectChatThread || $[18] !== userMatch) {
    t9 = async () => {
      if (teamPaywall && !teamPaywall.isUnlocked) {
        return;
      }
      setIsLoading(true);
      ;
      try {
        const threadInput = {
          memberUserIds: [userMatch.searchingUserId, userMatch.targetUserId]
        };
        const threadResult = await upsertDirectChatThread({
          variables: {
            input: threadInput
          }
        });
        matchingContext.setChatThreadId(threadResult.data.upsertDirectChatThread.thread.threadId);
        const commentInput = {
          threadId: threadResult.data.upsertDirectChatThread.thread.threadId,
          body: introChatMessage
        };
        await upsertChatComment({
          variables: {
            input: commentInput
          }
        });
        setError(null);
        props.afterSuccess();
      } catch (t10) {
        const err = t10;
        setError(err);
      }
      setIsLoading(false);
    };
    $[12] = introChatMessage;
    $[13] = matchingContext;
    $[14] = props;
    $[15] = teamPaywall;
    $[16] = upsertChatComment;
    $[17] = upsertDirectChatThread;
    $[18] = userMatch;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const onSubmit = t9;
  if (query.loading) {
    let t10;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Center padding="1em"><Spinner /></Center>;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    return t10;
  }
  if (!userMatch) {
    let t10;
    if ($[21] !== props.userMatchId) {
      t10 = <Center padding="1em"><Text>Could not load UserMatch:${props.userMatchId}</Text></Center>;
      $[21] = props.userMatchId;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    return t10;
  }
  let t10;
  if ($[23] !== onSubmit) {
    t10 = ev => {
      ev.preventDefault();
      onSubmit();
      return false;
    };
    $[23] = onSubmit;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== getBreakpointValue) {
    t11 = getBreakpointValue(["lg", "lg", "xl", "xl"]);
    $[25] = getBreakpointValue;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== userMatch.searchingUser.fullName) {
    t12 = <Avatar.Fallback name={userMatch.searchingUser.fullName} />;
    $[27] = userMatch.searchingUser.fullName;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== userMatch.searchingUser.pictureUrl) {
    t13 = <Avatar.Image src={userMatch.searchingUser.pictureUrl} />;
    $[29] = userMatch.searchingUser.pictureUrl;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11 || $[32] !== t12 || $[33] !== t13) {
    t14 = <Avatar.Root size={t11}>{t12}{t13}</Avatar.Root>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== getBreakpointValue) {
    t15 = getBreakpointValue(["80px", "80px", "96px", "96px"]);
    $[35] = getBreakpointValue;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== userMatch.matchScoreAsPercent) {
    t16 = Math.round(userMatch.matchScoreAsPercent);
    $[37] = userMatch.matchScoreAsPercent;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ProgressCircle.Circle css={{
      "--thickness": "6px"
    }}><ProgressCircle.Track /><ProgressCircle.Range stroke="green" /></ProgressCircle.Circle>;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t15 || $[41] !== t16) {
    t18 = <ProgressCircle.Root capIsRound={true} maxWidth={t15} size="md" trackColor="Purple50" value={t16}>{t17}</ProgressCircle.Root>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== getBreakpointValue) {
    t19 = getBreakpointValue(["lg", "lg", "xl", "xl"]);
    $[43] = getBreakpointValue;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== userMatch.targetUser.fullName) {
    t20 = <Avatar.Fallback name={userMatch.targetUser.fullName} />;
    $[45] = userMatch.targetUser.fullName;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== userMatch.targetUser.pictureUrl) {
    t21 = <Avatar.Image src={userMatch.targetUser.pictureUrl} />;
    $[47] = userMatch.targetUser.pictureUrl;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t19 || $[50] !== t20 || $[51] !== t21) {
    t22 = <Avatar.Root size={t19}>{t20}{t21}</Avatar.Root>;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t14 || $[54] !== t18 || $[55] !== t22) {
    t23 = <HStack flexGrow={1} gap={6} justify="space-between">{t14}{t18}{t22}</HStack>;
    $[53] = t14;
    $[54] = t18;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e => {
      setIntroChatMessage(e.target.value);
    };
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== introChatMessage || $[59] !== isLoading) {
    t25 = <AutoResizeTextarea disabled={isLoading} placeholder="Message" value={introChatMessage} onChange={t24} />;
    $[58] = introChatMessage;
    $[59] = isLoading;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  const t26 = teamPaywall?.contentPermission ?? "canAccessMemberMatching";
  const t27 = teamPaywall?.isUnlocked;
  const t28 = teamPaywall?.title;
  const t29 = teamPaywall?.upgradeProductId;
  let t30;
  if ($[61] !== isLoading) {
    t30 = <Button disabled={isLoading} loading={isLoading} type="submit" variant="cta" width="full">Send</Button>;
    $[61] = isLoading;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  const t31 = currentUser?.remainingMemberChatsCount;
  let t32;
  if ($[63] !== t31) {
    t32 = <Text fontSize="12px" opacity={0.5}>{"You can initiate "}<strong>{t31}{" more"}</strong>{" "}introductions today.</Text>;
    $[63] = t31;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] !== t30 || $[66] !== t32) {
    t33 = <VStack gap={2} width="full">{t30}{t32}</VStack>;
    $[65] = t30;
    $[66] = t32;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  let t34;
  if ($[68] !== t26 || $[69] !== t27 || $[70] !== t28 || $[71] !== t29 || $[72] !== t33) {
    t34 = <PaywalledAction contentPermission={t26} isUnlocked={t27} title={t28} upgradeCtaLabel="Upgrade to Send" upgradeProductId={t29} variant="button-only">{t33}</PaywalledAction>;
    $[68] = t26;
    $[69] = t27;
    $[70] = t28;
    $[71] = t29;
    $[72] = t33;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== error) {
    t35 = error && <Box>{"Error: "}{error.toString()}</Box>;
    $[74] = error;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== t23 || $[77] !== t25 || $[78] !== t34 || $[79] !== t35) {
    t36 = <VStack gap={5}>{t23}{t25}{t34}{t35}</VStack>;
    $[76] = t23;
    $[77] = t25;
    $[78] = t34;
    $[79] = t35;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] !== t10 || $[82] !== t36) {
    t37 = <form onSubmit={t10}>{t36}</form>;
    $[81] = t10;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  return t37;
};